import moment from 'moment-timezone';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, LoadingSkeleton, NumberLabel, useQueryFetch } from 'sonata-react-components';
import { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';

import { useInsertionOrderFormContext } from '@/components/ioSettings/InsertionOrderForm/InsertionOrderForm.provider';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { getBeginAndEndDateFromFlights } from '@/helpers/ioSettings/ioSettings.helper';
import { getScreensVolumetryAndForecast } from '@/services/common.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import './volumetryAndForecast.scss';

const getDays = (begin: moment.Moment | null, end: moment.Moment | null) => {
  let timeFrame = {
    end: moment.utc().startOf('day'),
    start: moment.utc().startOf('day').subtract(15, 'days')
  };

  if (begin && end) {
    timeFrame = {
      end: end.add(1, 'days').startOf('day'),
      start: begin.startOf('day')
    };
  }

  return timeFrame.end.diff(timeFrame.start, 'days');
};

type Props = {
  countries?: string[];
  days: number;
  screens: GeoDataAndScreensInteface[];
};

type ItemHighlightProps = {
  amount?: number;
  icon?: string;
  loading?: boolean;
  text: string;
};

function ItemHighlight({ amount, icon, loading = false, text }: ItemHighlightProps) {
  const user = useGlobalStore(getUser);
  return (
    <div className="itemHighlightStyled itemHighlight">
      <div className="itemHighlight__infoContainer">
        {icon && <i className={icon} />}
        <span className="itemHighlight__infoLabel">{text}</span>
      </div>
      {loading ? (
        <LoadingSkeleton count={1} width={100} />
      ) : (
        <NumberLabel
          amount={amount || 0}
          className="itemHighlight__amountLabel"
          country={user.toJSON().country.toLowerCase()}
          decimals={0}
          na={isNaN(amount!) || amount == null}
        />
      )}
    </div>
  );
}

function Content({ countries, days, screens }: Props) {
  const screensInventoryCount = useRef<number | null>(null);
  const { formatMessage } = useIntl();

  const forecastTimeFrame = {
    end: moment.utc().startOf('day'),
    start: moment.utc().startOf('day').subtract(days, 'days')
  };

  const country = countries?.[0].toUpperCase() || '';
  const deviceIds = screens?.map(screen => screen.device_id) || [];
  const beginDate = forecastTimeFrame.start.toISOString();
  const endDate = forecastTimeFrame.end.toISOString();

  const {
    data: forecastingData,
    isFetching,
    refetch
  } = useQueryFetch(
    ['volumetry_and_forecast', country, beginDate, endDate],
    () => {
      screensInventoryCount.current = screens.length;
      const sourceElementIds = screens?.filter(s => s._id).map(s => s._id);
      return getScreensVolumetryAndForecast({
        beginDate,
        country,
        deviceIds,
        endDate,
        sourceElementIds: sourceElementIds.length > 0 ? sourceElementIds : undefined
      });
    },
    {
      enabled: screens.length > 0 && !screensInventoryCount.current,
      keepPreviousData: true
    }
  );

  const itemHighlightConfigs = [
    {
      icon: 'fa-kit fa-solid-fa-audience-centric',
      keyProp: 'audience',
      text: formatMessage({
        defaultMessage: 'Estimated audience',
        id: 'multiFlightScreens.volumetryAndForecast.audience'
      })
    },
    {
      icon: 'far fa-smile',
      keyProp: 'impressions',
      text: formatMessage({
        defaultMessage: 'Estimated impressions',
        id: 'multiFlightScreens.volumetryAndForecast.impressions'
      })
    },
    {
      icon: 'fa-kit fa-screen-play',
      keyProp: 'playouts',
      text: formatMessage({
        defaultMessage: 'Estimated playouts',
        id: 'multiFlightScreens.volumetryAndForecast.playouts'
      })
    }
  ];

  return (
    <div className="volumetryAndForecastStyled volumetryAndForecast">
      <div className="volumetryAndForecast__items">
        <ItemHighlight
          amount={screensInventoryCount.current || 0}
          icon="fa-kit fa-dooh"
          loading={isFetching || !screensInventoryCount.current}
          text={formatMessage({
            defaultMessage: 'Screens inventory',
            id: 'multiFlightScreens.volumetryAndForecast.screens'
          })}
        />
        {itemHighlightConfigs.map(({ keyProp, ...props }) => (
          <ItemHighlight
            {...props}
            amount={forecastingData ? forecastingData[keyProp] : 0}
            key={keyProp}
            loading={isFetching}
          />
        ))}
        <ButtonUI
          icon="fas fa-sync-alt"
          iconPosition="left"
          loading={isFetching}
          onClick={refetch}
          shape="squareRounded"
          size="ultraSmall"
          type="secondary"
        />
      </div>
    </div>
  );
}

export function VolumetryAndForecastIO() {
  const { countries, flights, screens = [] } = useInsertionOrderFormContext();

  const { begin, end } = getBeginAndEndDateFromFlights(flights!);

  const days = getDays(begin, end);

  return <Content countries={countries} days={days} screens={screens} />;
}

export function VolumetryAndForecastLI() {
  const { beginDate: begin, countries, endDate: end, screens = [] } = useLineItemFormContext();

  const days = getDays(moment(begin), moment(end));

  return <Content countries={countries} days={days} screens={screens} />;
}
